import { getName, TSalesManager } from '@internal/services/TSalesManager'
import { useFeatureFlag } from '@internal/utils/featureFlags/useFeatureFlag'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { Stack, Typography } from '@renderer-ui-library/mui'
import classNames from 'classnames'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { ProfilePicture } from '../Media/ProfilePicture'
import styles from './contactUsCard.module.scss'

interface Props {
  salesManager: TSalesManager
}

export const ContactUsCard = ({ salesManager }: Props) => {
  const { t } = useTranslation(localeNamespace.common)

  const { hasFeature } = useFeatureFlag()
  const isZohoUsersFeatureEnabled = hasFeature('zohoUsers')

  const title = useMemo(() => {
    if (!isZohoUsersFeatureEnabled) {
      return salesManager.jobTitle
    }
    if (salesManager.bioTitle.startsWith(salesManager.jobTitle)) {
      return salesManager.bioTitle
    }
    return [salesManager.jobTitle, salesManager.bioTitle]
      .filter(Boolean)
      .join(', ')
  }, [isZohoUsersFeatureEnabled, salesManager.bioTitle, salesManager.jobTitle])

  const name = getName(salesManager)

  return (
    <div className={styles.container}>
      <div className={styles.imageWrapper}>
        <ProfilePicture
          salesManager={salesManager}
          variant='indoors'
          width={400}
          height={300}
        />
      </div>

      <Stack direction={'column'} spacing={2} className={styles.stack}>
        <div className={styles.contentInfoWrapper}>
          {name && (
            <Typography variant='h6' color='textPrimary'>
              {name}
            </Typography>
          )}
          {title && (
            <Typography variant='body1' color='textSecondary'>
              {title}
            </Typography>
          )}
        </div>

        {isZohoUsersFeatureEnabled && salesManager.bioText && (
          <div>
            <Typography variant='body1'>{salesManager.bioText}</Typography>
          </div>
        )}

        <div className={classNames(styles.contactWrapper)}>
          <div className={styles.contentInfoWrapper}>
            <Typography variant='body1'>
              <strong>{`${t(translations.languagesKey)}: `}</strong>
              <span>{salesManager.languagesSpoken}</span>
            </Typography>
          </div>

          <div className={classNames(styles.contentInfoWrapper)}>
            <Typography variant='body1'>
              <strong>{`${t(translations.emailKey)}: `}</strong>
              <a href={`mailto:${salesManager.email}`} className={styles.link}>
                {salesManager.email}
              </a>
            </Typography>
          </div>
        </div>
      </Stack>
    </div>
  )
}

ContactUsCard.displayName = 'ContactUsCard'
